import { HandRaisedIcon as HandIcon } from "@heroicons/react/24/outline";
import { SCRAPE_STATUS } from "../api/Profiles";
import { ReportSummaryItemApi } from "../api/Reports";
import Badge, { BadgeProps } from "../shared-components/Badge";
import { useResumeFailedWorkflowRun } from "../api/WorkflowApi";
import { useConfirmationDialog } from "../shared-components/ConfirmationDialog";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import { useEffect } from "react";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import { WORKFLOW_STEP_STATUS_ENUM, workStepNameToUI } from "../api/Workflows";
import { classNames } from "../utils";

function StatusBadge(props: BadgeProps) {
  return <Badge {...props} hasDot={false} />;
}

interface EntryProps {
  stepStatusName: string | null;
  workflowRunUUID?: string;
  failed?: boolean;
  title?: string;
  report?: ReportSummaryItemApi;
  allowResumeOnClick?: boolean;
}

const ReportStatus = ({
  stepStatusName,
  workflowRunUUID,
  failed = false,
  report,
  allowResumeOnClick = false,
}: EntryProps) => {
  const { mutate: resumeRun, error: resumeWorkflowError } =
    useResumeFailedWorkflowRun();
  const confirmationDialog = useConfirmationDialog();
  const { canResumeWorkflow } = usePermissionValidator();
  const canTriggerResume = allowResumeOnClick && canResumeWorkflow;

  const handleResumeWorkflow = async () => {
    const uuid = report?.workflow_run_uuid || workflowRunUUID;
    if (uuid) {
      try {
        await resumeRun(uuid);
        confirmationDialog.hide();
      } catch (error) {
        console.error("Failed to resume the failed workflow:", error);
      }
    }
  };

  const handleStatusClick = () => {
    if (canTriggerResume) {
      confirmationDialog.show(
        "Restart Workflow",
        "Resume workflow from failed steps?",
        (action) => {
          if (action === "continue") {
            handleResumeWorkflow();
          }
        }
      );
    }
  };

  useEffect(() => {
    if (resumeWorkflowError) {
      confirmationDialog.show(
        "Error Restarting the Workflow",
        "",
        () => {},
        <div className="my-2">
          <ErrorMessageContainer>
            <>
              <strong className="block py-1">
                {report?.workflow_run_uuid}
              </strong>
              Please try again or contact the administrator.
            </>
          </ErrorMessageContainer>
        </div>,
        false
      );
    }
  }, [resumeWorkflowError, report, confirmationDialog]);

  const stepStatusNameUI = workStepNameToUI(
    stepStatusName as WORKFLOW_STEP_STATUS_ENUM
  );

  return (
    <>
      <div
        className={
          canTriggerResume
            ? "cursor-pointer select-none flex items-center"
            : "select-none flex items-center bg-f"
        }
        onClick={handleStatusClick}
      >
        {report?.step_payload.MANUAL_SCRAPE_READY ===
          SCRAPE_STATUS.IN_PROGRESS &&
          report.step_name === WORKFLOW_STEP_STATUS_ENUM.PostsManualScrape && (
            <span>
              <HandIcon className="text-green-400 w-4" />
            </span>
          )}
        <StatusBadge
          className={classNames(
            failed ? "cursor-pointer select-none" : "",
            failed ? "bg-red-200 text-red-800" : "",
            !failed
              ? `${stepStatusNameUI?.color} ${stepStatusNameUI?.textColor}`
              : ""
          )}
          title={
            failed
              ? `Workflow failed on ${stepStatusNameUI?.name ?? "N/A"}`
              : ""
          }
          color={failed ? "red" : ""}
        >
          {failed ? "ERROR" : stepStatusNameUI?.name ?? "N/A"}
        </StatusBadge>
      </div>
      {confirmationDialog.modal}
    </>
  );
};

export default ReportStatus;
